import { Stripe, StripeElements } from '@stripe/stripe-js';
import * as Yup from 'yup';
import { callAPI } from '../../../services/api.service';
import { CONFIG } from '../../../config/mainConfig';
import { CardElement } from '@stripe/react-stripe-js';
import { Toast } from '../../../components/Common/Toast';
import { getURLSearchParams, goToTopOfPage } from '../../../helper/helper';
import { trackConversion } from 'goaffpro-client';
import { BookModel } from '../../../interfaces/book.interface';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { CouponTypesEnum } from '../../../interfaces/coupon.interface';
import { UserModel, UserShippingAddress } from '../../../interfaces/user.interface';
import { CurrencyEnum } from '../../../interfaces/plan.interface';

// Tailors the purchase confirmation so the buyer knows whether the book ships automatically or
// joins the classroom's one-time Class Shipment. `autoShipped` comes from the place-order
// response. The non-auto message speaks to the BUYER (typically a parent on a share link): the
// send action belongs to the teacher — the old copy told buyers to click a teacher-only button
// that had also been renamed.
const purchaseSuccessMessage = (autoShipped?: boolean): string =>
  autoShipped === true
    ? 'Book purchased! It will be printed and shipped to your address — no further action needed.'
    : autoShipped === false
      ? 'Book purchased! It’s reserved for the class’s one-time Class Shipment — the teacher sends it to the school, and you don’t need to do anything.'
      : 'Book Purchased Successfully';

interface HandleSubmitParams {
  values: Record<string, string | number>;
  validationSchema: Yup.ObjectSchema<any>;
  stripe: Stripe | null;
  elements: StripeElements | null;
  user: UserModel | null;
  bookId: string | undefined;
  bookQty: number;
  additionalPageCost: number;
  bookTotalCost: number | null;
  shippingCost: number;
  setPaymentLoader: (state: boolean) => void;
  setPurchaseSuccess: (state: boolean) => void;
  setErrors: (errors: any) => void;
  isShare?: boolean;
  bookDetails?: BookModel | null;
  selectedCurrency?: CurrencyEnum;
}

interface ApplyPromoCodeParams {
  couponCode: string;
  bookTotalCost: number | null;
  userEmail: string | undefined;
  setCouponClass: (className: string) => void;
  setDiscount: (discount: number | null) => void;
  setCouponDescription: (description: string) => void;
  setCouponType: (couponType: string) => void;
  setCouponLoader: (state: boolean) => void;
}

export const handlePurchaseSubmit = async ({
  values,
  validationSchema,
  stripe,
  elements,
  user,
  bookId,
  bookQty,
  additionalPageCost,
  bookTotalCost,
  shippingCost,
  setPaymentLoader,
  setPurchaseSuccess,
  setErrors,
  isShare,
  bookDetails,
  selectedCurrency,
}: HandleSubmitParams) => {
  try {
    const validation = await validationSchema.validate(values, { abortEarly: false });
    console.log(validation);

    const processOrder = async (paymentMethodId?: string) => {
      // Get currency from selected country or user preferences
      const currency = selectedCurrency || user?.detectedCurrency || user?.preferredCurrency || CurrencyEnum.USD;
      
      let requestData = {
        paymentMethod: paymentMethodId,
        bookId,
        shippingEmail: user?.email || values?.shippingEmail,
        coupon: values?.coupon?.toString().toUpperCase(),
        bookQty,
        requestType: CouponTypesEnum.FOR_BOOK_PURCHASE,
        additionalPageCost,
        currency,
      };

      if (!bookDetails?.isStudentBook) {
        requestData = {
          ...requestData,
          ...{
            customerName: values?.customerName,
            shippingAddress1: values?.shippingAddress1,
            shippingAddress2: values?.shippingAddress2,
            shippingState: values?.shippingState,
            shippingCountry: values?.shippingCountry,
            shippingCity: values?.shippingCity,
            shippingZip: values?.shippingZip,
            billingCardHolderName: values?.billingCardHolderName,
            billingAddress1: values?.billingAddress1,
            billingAddress2: values?.billingAddress2,
            billingCity: values?.billingCity,
            billingState: values?.billingState,
            billingPostalCode: values?.billingPostalCode,
          },
        };
      }
      const queryParams = getURLSearchParams();
      const response = !isShare
        ? await callAPI('api/book-purchase/place-order', requestData)
        : await callAPI('api/book-purchase/public/place-order', requestData, 'POST', {
            share: queryParams.get('share'),
          });
      return response;
    };

    setErrors({});

    if (stripe && shippingCost > 0) {
      setPaymentLoader(true);
      const card = elements?.getElement(CardElement);

      const resultStripe =
        (bookTotalCost || 0) > 0
          ? card &&
            (await stripe.createPaymentMethod({
              type: 'card',
              card,
              billing_details: { email: user?.email },
            }))
          : null;

      if (resultStripe?.error) {
        setPaymentLoader(false);
        Toast(resultStripe.error.message, true);
      } else {
        const paymentResponse = await processOrder(resultStripe?.paymentMethod?.id);
        if (paymentResponse?.error || paymentResponse?.status !== HTTPStatusEnum.OK) {
          Toast(paymentResponse?.error || paymentResponse?.message, true);
        } else {
          Toast(purchaseSuccessMessage(paymentResponse?.autoShipped));
          setPurchaseSuccess(true);
          // Get user's currency for tracking
          const userCurrency = selectedCurrency || user?.detectedCurrency || user?.preferredCurrency || CurrencyEnum.USD;
          trackConversion({
            number: paymentResponse?.paymentId,
            total: bookTotalCost!,
            currency: userCurrency,
          });
          goToTopOfPage();
        }
      }
    } else {
      // Payment for free physical book
      const paymentResponse = await processOrder();
      if (paymentResponse?.error || paymentResponse?.status !== HTTPStatusEnum.OK) {
        Toast(paymentResponse?.error || paymentResponse?.message, true);
      } else {
        Toast(purchaseSuccessMessage(paymentResponse?.autoShipped));
        setPurchaseSuccess(true);
        goToTopOfPage();
      }
    }

    setPaymentLoader(false);
  } catch (error: any) {
    if (error?.name === 'ValidationError') {
      const yupErrors: Record<string, string> = {};
      error.inner.forEach((err: any) => {
        yupErrors[err.path] = err.message;
      });
      setErrors(yupErrors);
    }
  }
};

export const handleApplyPromoCode = async ({
  couponCode,
  bookTotalCost,
  userEmail,
  setCouponClass,
  setDiscount,
  setCouponDescription,
  setCouponType,
  setCouponLoader,
}: ApplyPromoCodeParams) => {
  setCouponLoader(true);

  try {
    const response = await callAPI('user/coupon-validate', {
      couponCode,
      amount: parseFloat(String(bookTotalCost)),
      requestType: CouponTypesEnum.FOR_BOOK_PURCHASE,
      email: userEmail,
    });

    if (response?.status === HTTPStatusEnum.OK) {
      setCouponClass('text-storygreen');
      const discountedPrice = response?.data?.discount;
      setDiscount(discountedPrice);
      setCouponDescription(response?.data?.couponDescription);
      setCouponType(response?.data?.couponType);
      Toast('Coupon Applied');
    } else {
      setDiscount(null);
      setCouponClass('text-storyred');
      Toast('Invalid Coupon', true);
    }
  } catch (error) {
    Toast('Error applying coupon. Please try again.', true);
  } finally {
    setCouponLoader(false);
  }
};

export const getBookDetailsService = async (
  bookId: string,
  isShare?: boolean,
): Promise<{
  book: BookModel | null;
  readonlyShipping?: boolean;
  shippingAddress?: UserShippingAddress | null;
  additionalPageCost: number;
  totalPages: any;
}> => {
  try {
    const queryParams = getURLSearchParams();
    const bookDetails = !isShare
      ? await callAPI(`api/getBookDetails/${bookId}`)
      : await callAPI(`api/bookDetails/${bookId}`, null, 'GET', { share: queryParams.get('share') });
    if (bookDetails?.book) {
      const book = bookDetails?.book;
      const totalPages = book?.pageTotal || 0;
      const readonlyShipping = bookDetails?.readonlyShipping || false;
      const shippingAddress = bookDetails?.shippingAddress || {};
      const additionalPageCost =
        totalPages > 32 ? parseFloat((((totalPages - 32) / 4) * CONFIG.BILLING.ADDITIONAL_PAGE_FEE).toFixed(2)) : 0;
      return {
        book,
        additionalPageCost,
        totalPages,
        readonlyShipping,
        shippingAddress,
      };
    }
    return { book: null, additionalPageCost: 0, totalPages: 0 };
  } catch (error) {
    return { book: null, additionalPageCost: 0, totalPages: 0 };
  }
};
